import {Button, IconButton, Link, SelectPanel, type SelectPanelItemInput} from '@primer/react'
import type {HelpUrls, RegisteredRuleSchemaComponent} from '../../types/rules-types'
import {Blankslate} from '../Blankslate'
import {PlusIcon, TrashIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useCallback, useMemo, useState} from 'react'
import {useDebounce} from '@github-ui/use-debounce'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'

export function RequiredDeployments({value, onValueChange, readOnly, helpUrls}: RegisteredRuleSchemaComponent) {
  const environments = value as string[]
  const {resolvePath} = useRelativeNavigation()

  const getEnvironmentSuggestions = useCallback(
    async (filter: string) => {
      const response = await verifiedFetch(
        resolvePath(`../deployment_environment_suggestions.json?query=${encodeURIComponent(filter)}`),
      )

      if (!response.ok) {
        return []
      }

      return (await response.json()) as string[]
    },
    [resolvePath],
  )

  return (
    <>
      {!readOnly ? (
        <div className="Box">
          <div className="Box-header d-flex flex-justify-between flex-items-center">
            <div className="Box-title">Required deployment environments</div>
            <DeploymentPicker
              value={environments}
              onValueChange={onValueChange}
              getEnvironmentSuggestions={getEnvironmentSuggestions}
            />
          </div>
          <DeploymentRows
            environments={environments}
            onValueChange={onValueChange}
            readOnly={readOnly}
            helpUrls={helpUrls}
          />
        </div>
      ) : (
        <DeploymentRows
          environments={environments}
          onValueChange={onValueChange}
          readOnly={readOnly}
          helpUrls={helpUrls}
        />
      )}
    </>
  )
}

function DeploymentPicker({
  value,
  onValueChange,
  getEnvironmentSuggestions,
}: {
  value: string[]
  onValueChange: (value: string[]) => void
  getEnvironmentSuggestions: (filter: string) => Promise<string[]>
}) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])

  const items: SelectPanelItemInput[] = useMemo(() => {
    const mappedSuggestions = suggestions.map(s => ({
      id: s,
      text: s,
      onAction: () => {
        const alreadySelected = value.includes(s)
        if (alreadySelected) {
          onValueChange(value.filter(u => u !== s))
        } else {
          onValueChange([...value, s])
        }
      },
    }))
    return mappedSuggestions
  }, [suggestions, value, onValueChange])

  const selectedItems = useMemo(() => {
    return items.filter(item => value.includes(item.id as string))
  }, [items, value])

  const debounceSuggestions = useDebounce(async (newFilter: string) => {
    setIsLoading(true)
    setSuggestions(await getEnvironmentSuggestions(newFilter))
    setIsLoading(false)
  }, 200)

  const onOpenChange = async () => {
    if (!open) {
      setFilter('')
      setOpen(true)
      setIsLoading(true)
      setSuggestions(await getEnvironmentSuggestions(''))
      setIsLoading(false)
    } else {
      setOpen(false)
    }
  }

  return (
    <SelectPanel
      title="Add environments"
      loading={isLoading}
      open={open}
      onOpenChange={onOpenChange}
      filterValue={filter}
      onFilterChange={f => {
        setFilter(f)
        debounceSuggestions(f)
      }}
      items={items}
      selected={selectedItems}
      placeholderText="Select environments"
      renderAnchor={anchorProps => {
        return (
          <Button
            trailingAction={TriangleDownIcon}
            leadingVisual={PlusIcon}
            aria-label="Click to add environments"
            {...anchorProps}
          >
            Add environments
          </Button>
        )
      }}
      message={
        items.length === 0
          ? {
              title: `No environments found`,
              variant: 'empty',
              body: '',
            }
          : undefined
      }
      onSelectedChange={() => {}} // using onAction
    />
  )
}

function DeploymentRows({
  environments,
  onValueChange,
  readOnly,
  helpUrls,
}: {
  environments: string[]
  onValueChange?: (value: string[]) => void
  readOnly?: boolean
  helpUrls?: HelpUrls
}) {
  return (
    <>
      {environments.length ? (
        <ul>
          {environments.map(environment => (
            <li key={environment} className="Box-row d-flex flex-justify-between flex-items-center">
              <div>
                <span className="ml-2 wb-break-word">{environment}</span>
              </div>
              {!readOnly && (
                <IconButton
                  className="ml-2"
                  icon={TrashIcon}
                  type="button"
                  aria-label={`Delete ${environment}`}
                  size="small"
                  variant="invisible"
                  onClick={() => {
                    onValueChange?.(environments.filter(toDelete => toDelete !== environment))
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Blankslate heading="No deployment environments have been added">
          {!readOnly && helpUrls && (
            <Link target="_blank" href={helpUrls.deploymentEnvironments}>
              Learn more about deployment environments
            </Link>
          )}
        </Blankslate>
      )}
    </>
  )
}
